import React, { useState } from 'react';

/**
 * Status Tooltip Component
 * Shows helpful information about campaign/content statuses
 */
const StatusTooltip = ({ status, type = 'campaign', children }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  // Status information - Clean & Minimal (with pre-approval workflow)
  const campaignStatuses = {
    pending: {
      label: 'Pending Review',
      color: 'yellow',
      info: 'Brand reviewing your application (1-3 days)'
    },
    approved: {
      label: 'Approved',
      color: 'green',
      info: 'You\'re selected! Submit content draft for approval'
    },
    rejected: {
      label: 'Not Selected',
      color: 'red',
      info: 'Brand selected other influencers'
    },
    draft_submitted: {
      label: 'Draft Submitted',
      color: 'blue',
      info: 'Brand reviewing your content draft (1-2 days)'
    },
    draft_approved: {
      label: 'Draft Approved',
      color: 'green',
      info: 'Draft approved! Post content and submit proof'
    },
    draft_rejected: {
      label: 'Draft Needs Revision',
      color: 'orange',
      info: 'Brand needs changes - check feedback and resubmit'
    },
    posted: {
      label: 'Content Posted',
      color: 'blue',
      info: 'Submit proof of your posted content'
    },
    proof_submitted: {
      label: 'Proof Submitted',
      color: 'blue',
      info: 'Brand reviewing your proof (1-2 days)'
    },
    revision_requested: {
      label: 'Revision Requested',
      color: 'orange',
      info: 'Brand needs changes - check feedback'
    },
    content_approved: {
      label: 'Content Approved',
      color: 'green',
      info: 'Approved! Payment processing (3-5 days)'
    },
    payment_processing: {
      label: 'Payment Processing',
      color: 'blue',
      info: 'Payment arriving in 3-5 business days'
    },
    completed: {
      label: 'Completed',
      color: 'green',
      info: 'Campaign done, payment received'
    },
    active: {
      label: 'Active',
      color: 'green',
      info: 'Campaign is currently running'
    }
  };

  const contentStatuses = {
    submitted: {
      label: 'Under Review',
      color: 'blue',
      info: 'Brand reviewing (1-2 days)'
    },
    verified: {
      label: 'Auto-Verified',
      color: 'green',
      info: 'All checks passed - awaiting final approval'
    },
    needs_review: {
      label: 'Needs Review',
      color: 'yellow',
      info: 'Warnings detected - brand reviewing'
    },
    approved: {
      label: 'Approved',
      color: 'green',
      info: 'Approved! Payment processing (3-5 days)'
    }
  };

  const statuses = type === 'campaign' ? campaignStatuses : contentStatuses;
  const statusInfo = statuses[status] || {
    label: status,
    color: 'gray',
    info: 'Status information not available'
  };

  const getColorClasses = (color) => {
    const colors = {
      green: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        border: 'border-green-200',
        badge: 'bg-green-500'
      },
      blue: {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        border: 'border-blue-200',
        badge: 'bg-blue-500'
      },
      yellow: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        border: 'border-yellow-200',
        badge: 'bg-yellow-500'
      },
      orange: {
        bg: 'bg-orange-100',
        text: 'text-orange-800',
        border: 'border-orange-200',
        badge: 'bg-orange-500'
      },
      red: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        border: 'border-red-200',
        badge: 'bg-red-500'
      },
      gray: {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        border: 'border-gray-200',
        badge: 'bg-gray-500'
      }
    };
    return colors[color] || colors.gray;
  };

  const colorClasses = getColorClasses(statusInfo.color);

  return (
    <div className="relative inline-block">
      {/* Status Badge with Hover */}
      <div
        className="cursor-help"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {children || (
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colorClasses.bg} ${colorClasses.text}`}>
            <span className="mr-1">{statusInfo.icon}</span>
            {statusInfo.label}
            <svg className="ml-1 w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </span>
        )}
      </div>

      {/* Tooltip - Clean & Minimal */}
      {showTooltip && (
        <div className="absolute z-50 w-64 p-3 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 left-0">
          <div className="absolute -top-2 left-4 w-4 h-4 bg-white border-l border-t border-gray-200 transform rotate-45"></div>
          <div className="relative">
            <h4 className="font-semibold text-gray-900 mb-2">{statusInfo.label}</h4>
            <p className="text-sm text-gray-600">{statusInfo.info}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusTooltip;
